import styled from 'styled-components'

/**
 * Portal wrapper for the modal
 * Uses class name for print style targeting
 */
export const ModalPortal = styled.div.attrs({
  className: 'print-modal-portal',
})`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ theme }) => theme.zIndex.modal};
`

/**
 * Semi-transparent overlay behind the modal
 */
export const ModalOverlay = styled.div.attrs({
  className: 'print-modal-overlay',
})`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
`

/**
 * Main modal content container
 */
export const ModalContent = styled.div.attrs({
  className: 'print-modal-content',
})`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  display: flex;
  flex-direction: column;
  max-width: 95vw;
  max-height: 95vh;
  width: 1400px;
  overflow: hidden;
`

/**
 * Modal header with title and subtitle
 * Hidden in print mode
 */
export const ModalHeader = styled.div.attrs({
  className: 'modal-header screen-only',
})`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.large};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

/**
 * Modal title text
 */
export const ModalTitle = styled.h2`
  margin: 0;
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  font-size: ${({ theme }) => theme.typography.fontSize.title};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

/**
 * Modal subtitle text
 */
export const ModalSubtitle = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
`

/**
 * Scrollable body area containing the preview
 */
export const ModalBody = styled.div.attrs({
  className: 'modal-body',
})`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.secondary};

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.text.tertiary};
  }

  @media print {
    padding: 0 !important;
    overflow: visible !important;
    display: block !important;
    background: transparent !important;
  }
`

/**
 * Container for the PDF preview content
 * Fixed 1200px width for consistent print output
 * Uses CSS zoom instead of transform for proper Mapbox marker positioning
 */
export const PreviewContainer = styled.div.attrs({
  className: 'print-report',
})`
  width: 1200px;
  max-width: 1200px;
  margin: 0 auto;
  transform-origin: top center;
  transition: zoom 0.2s ease-out;

  @media print {
    zoom: 1 !important;
    margin: 0 !important;
  }
`

/**
 * Modal footer with action buttons
 * Hidden in print mode
 */
export const ModalFooter = styled.div.attrs({
  className: 'modal-actions screen-only',
})`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing.medium}
    ${({ theme }) => theme.spacing.large};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
`

/**
 * Container for zoom controls
 */
export const ZoomControls = styled.div.attrs({
  className: 'zoom-controls screen-only',
})`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.small};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
`

/**
 * Zoom preset button
 */
export const ZoomPresetButton = styled.button<{ $isActive: boolean }>`
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary : theme.colors.text.secondary};
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primaryLight : 'transparent'};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme, $isActive }) =>
      $isActive ? theme.colors.primaryLight : theme.colors.background.tertiary};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`

/**
 * Zoom level display label
 */
export const ZoomLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  min-width: 48px;
  text-align: center;
  font-family: ${({ theme }) => theme.typography.fontFamily.mono};
  cursor: pointer;
  user-select: none;
`

/**
 * Divider between zoom presets and +/- buttons
 */
export const ZoomDivider = styled.div`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: 0 ${({ theme }) => theme.spacing.xsmall};
`

/**
 * Action buttons container
 */
export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

/**
 * Page indicator showing page count
 */
export const PageIndicator = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.small};
  margin: ${({ theme }) => theme.spacing.medium} 0;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};

  @media print {
    display: none !important;
  }
`

/**
 * Page wrapper for preview (adds visual separation between pages)
 * A4 aspect ratio: 210mm × 297mm = 1:1.414
 * At 1200px width, height = 1200 × 1.414 = 1697px
 */
export const PageWrapper = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-bottom: ${({ theme }) => theme.spacing.large};
  overflow: hidden;
  width: 1200px;
  min-height: 1697px;

  @media print {
    box-shadow: none !important;
    margin-bottom: 0 !important;
    border-radius: 0 !important;
    min-height: auto !important;
  }
`
